import {
  useBlocksActions,
  useCellActions,
  useSelectedCell,
} from '@/models/commercial-proposals/blocks.store';
import { Box, BoxProps } from '@mantine/core';
import { ReactNode, useState } from 'react';
import { createPortal } from 'react-dom';

export const cellId = (
  params: { blockId: string } & Partial<{
    serviceId: number;
    serviceWeight: number; // row
    tier: number; // column
    suffix: string;
  }>,
) => {
  const { blockId, serviceId, serviceWeight, tier, suffix } = params;
  return [blockId, serviceId, serviceWeight, tier, suffix]
    .filter((v) => v !== undefined && v !== null)
    .join('.');
};

export const parseCellId = (id: string) => {
  const [blockId, serviceId, serviceWeight, tier, suffix] = id.split('.');
  return {
    blockId,
    serviceId: Number(serviceId),
    serviceWeight: Number(serviceWeight),
    tier: Number(tier),
    suffix,
  };
};

export default function CellBase({
  children,
  id,
  settings,
  ...props
}: {
  children: React.ReactNode;
  id: string;
  settings: ReactNode;
} & BoxProps) {
  const [hover, setHover] = useState(false);
  const { select: selectCell } = useCellActions();
  const { select: selectBlock } = useBlocksActions();
  const [blockId] = id.split('.');

  const selectedCell = useSelectedCell();
  const isSelected = selectedCell === id;

  const renderSettingsPortal = () => {
    if (!isSelected || !settings) return null;

    const portalTarget = document.getElementById('settings-portal');
    if (!portalTarget) return null;

    return createPortal(settings, portalTarget);
  };

  return (
    <>
      <Box
        p={5}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        onClick={() => {
          if (isSelected) {
            selectCell(null);
          } else {
            selectBlock(blockId);
            selectCell(id);
          }
        }}
        style={{
          ...(isSelected
            ? {
                cursor: 'pointer',
                backgroundColor: 'var(--mantine-color-blue-light)',
                boxShadow: 'inset 0px 0px 0px 2px var(--mantine-color-blue-2)',
              }
            : {
                cursor: 'pointer',
                backgroundColor: hover
                  ? 'light-dark(var(--mantine-color-gray-1), var(--mantine-color-dark-4))'
                  : 'transparent',
                boxShadow: hover
                  ? 'light-dark(inset 0 0 0 2px rgba(0, 0, 0, 0.05), inset 0 0 0 2px rgba(255, 255, 255, 0.1))'
                  : 'none',
              }),
        }}
        {...props}
      >
        {children}
      </Box>
      {renderSettingsPortal()}
    </>
  );
}
